import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardPage from "./pages/DashboardPage";
import DepartmentsPage from "./pages/DepartmentsPage";
import DepartmentDetailPage from "./pages/DepartmentDetailPage";
import ChatPage from "./pages/ChatPage";
import SettingsPage from "./pages/SettingsPage";
import PrivacyDashboardPage from "./pages/PrivacyDashboardPage";
import AuditLogPage from "./pages/AuditLogPage";
import UsersPage from "./pages/UsersPage";
import OllamaGuidePage from "./pages/OllamaGuidePage";
import WeatherPage from "./pages/WeatherPage";
import WhatsAppPage from "./pages/WhatsAppPage";
import LearningPage from "./pages/LearningPage";
import FieldMarshalPage from "./pages/FieldMarshalPage";
import FleetioPage from "./pages/FleetioPage";
import TitanGPSPage from "./pages/TitanGPSPage";
import ServiceTrackingPage from "./pages/ServiceTrackingPage";
import FormsPage from "./pages/FormsPage";
import FormFillPage from "./pages/FormFillPage";
import FormBuilderPage from "./pages/FormBuilderPage";
import FormViewPage from "./pages/FormViewPage";
import FormSubmissionsPage from "./pages/FormSubmissionsPage";
import FormAnalyticsPage from "./pages/FormAnalyticsPage";
import SafetyAnalyticsPage from "./pages/SafetyAnalyticsPage";
import FormEditPage from "./pages/FormEditPage";
import FormLogPage from "./pages/FormLogPage";
import ClearSkyLogPage from "./pages/ClearSkyLogPage";
import ScaleReceiptLogPage from "./pages/ScaleReceiptLogPage";
import DispatchPage from "./pages/DispatchPage";
import DispatchManagementPage from "./pages/DispatchManagementPage";
import DispatchBoardPage from "./pages/DispatchBoardPage";
import JobsPage from "./pages/JobsPage";
import DriversPage from "./pages/DriversPage";
import CustomUnitsPage from "./pages/CustomUnitsPage";
import InvoiceReviewPage from "./pages/InvoiceReviewPage";
import PartsInventoryPage from "./pages/PartsInventoryPage";
import MaintenancePredictorPage from "./pages/MaintenancePredictorPage";
import ShopAssistantPage from "./pages/ShopAssistantPage";
import ClientTicketsPage from "./pages/ClientTicketsPage";
import ClientsPage from "./pages/ClientsPage";
import RepairsPage from "./pages/RepairsPage";
import FuelDashboardPage from "./pages/FuelDashboardPage";
import WrenchBoardPage from "./pages/WrenchBoardPage";
import MemberDashboardPage from "./pages/MemberDashboardPage";
import ShopOrdersPage from "./pages/ShopOrdersPage";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Restricts access to admin (and optionally manager). Redirects non-eligible
// users back to their role-aware dashboard.
const RoleProtectedRoute = ({ children, allowManager = false }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  const role = user?.role;
  const allowed = role === 'admin' || (allowManager && role === 'manager');
  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Picks the right dashboard component based on role.
const RoleAwareDashboard = () => {
  const { user } = useAuth();
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  return isAdminOrManager ? <DashboardPage /> : <MemberDashboardPage />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="min-h-screen bg-background">
          <div className="noise-overlay" />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            
            <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<RoleAwareDashboard />} />
              <Route path="departments" element={<DepartmentsPage />} />
              <Route path="departments/:departmentId" element={<DepartmentDetailPage />} />
              <Route path="departments/:departmentId/chat" element={<ChatPage />} />
              <Route path="settings" element={<RoleProtectedRoute><SettingsPage /></RoleProtectedRoute>} />
              <Route path="privacy" element={<RoleProtectedRoute allowManager={true}><PrivacyDashboardPage /></RoleProtectedRoute>} />
              <Route path="audit-logs" element={<RoleProtectedRoute allowManager={true}><AuditLogPage /></RoleProtectedRoute>} />
              <Route path="users" element={<RoleProtectedRoute><UsersPage /></RoleProtectedRoute>} />
              <Route path="ollama-guide" element={<RoleProtectedRoute><OllamaGuidePage /></RoleProtectedRoute>} />
              <Route path="weather" element={<WeatherPage />} />
              <Route path="whatsapp" element={<RoleProtectedRoute allowManager={true}><WhatsAppPage /></RoleProtectedRoute>} />
              <Route path="learning" element={<RoleProtectedRoute allowManager={true}><LearningPage /></RoleProtectedRoute>} />
              <Route path="fieldmarshal" element={<RoleProtectedRoute allowManager={true}><FieldMarshalPage /></RoleProtectedRoute>} />
              <Route path="fleetio" element={<RoleProtectedRoute allowManager={true}><FleetioPage /></RoleProtectedRoute>} />
              <Route path="titan-gps" element={<RoleProtectedRoute allowManager={true}><TitanGPSPage /></RoleProtectedRoute>} />
              <Route path="service-tracking" element={<RoleProtectedRoute allowManager={true}><ServiceTrackingPage /></RoleProtectedRoute>} />
              <Route path="forms" element={<FormsPage />} />
              <Route path="forms/fill/:templateId" element={<FormFillPage />} />
              <Route path="forms/builder" element={<RoleProtectedRoute allowManager={true}><FormBuilderPage /></RoleProtectedRoute>} />
              <Route path="forms/builder/:templateId" element={<RoleProtectedRoute allowManager={true}><FormBuilderPage /></RoleProtectedRoute>} />
              <Route path="forms/view/:submissionId" element={<FormViewPage />} />
              <Route path="forms/edit/:submissionId" element={<FormEditPage />} />
              <Route path="forms/submissions" element={<FormSubmissionsPage />} />
              <Route path="forms/analytics" element={<RoleProtectedRoute allowManager={true}><FormAnalyticsPage /></RoleProtectedRoute>} />
              <Route path="forms/analytics/:formType" element={<RoleProtectedRoute allowManager={true}><SafetyAnalyticsPage /></RoleProtectedRoute>} />
              <Route path="forms/log/:templateId" element={<FormLogPage />} />
              <Route path="clearsky-log" element={<RoleProtectedRoute allowManager={true}><ClearSkyLogPage /></RoleProtectedRoute>} />
              <Route path="scale-receipts" element={<RoleProtectedRoute allowManager={true}><ScaleReceiptLogPage /></RoleProtectedRoute>} />
              <Route path="dispatch" element={<RoleProtectedRoute allowManager={true}><DispatchBoardPage /></RoleProtectedRoute>} />
              <Route path="dispatch/legacy" element={<RoleProtectedRoute allowManager={true}><DispatchManagementPage /></RoleProtectedRoute>} />
              <Route path="dispatch/slack" element={<RoleProtectedRoute allowManager={true}><DispatchPage /></RoleProtectedRoute>} />
              <Route path="dispatch/jobs" element={<RoleProtectedRoute allowManager={true}><JobsPage /></RoleProtectedRoute>} />
              <Route path="dispatch/drivers" element={<RoleProtectedRoute allowManager={true}><DriversPage /></RoleProtectedRoute>} />
              <Route path="dispatch/units" element={<RoleProtectedRoute allowManager={true}><CustomUnitsPage /></RoleProtectedRoute>} />
              <Route path="dispatch/invoice-review" element={<RoleProtectedRoute allowManager={true}><InvoiceReviewPage /></RoleProtectedRoute>} />
              <Route path="client-tickets" element={<RoleProtectedRoute allowManager={true}><ClientTicketsPage /></RoleProtectedRoute>} />
              <Route path="clients" element={<RoleProtectedRoute allowManager={true}><ClientsPage /></RoleProtectedRoute>} />
              <Route path="repairs" element={<RoleProtectedRoute><RepairsPage /></RoleProtectedRoute>} />
              <Route path="vehicles" element={<RoleProtectedRoute allowManager={true}><WrenchBoardPage /></RoleProtectedRoute>} />
              <Route path="fuel" element={<RoleProtectedRoute allowManager={true}><FuelDashboardPage /></RoleProtectedRoute>} />
              <Route path="parts" element={<RoleProtectedRoute allowManager={true}><PartsInventoryPage /></RoleProtectedRoute>} />
              <Route path="maintenance-predictor" element={<RoleProtectedRoute allowManager={true}><MaintenancePredictorPage /></RoleProtectedRoute>} />
              <Route path="shop-assistant" element={<RoleProtectedRoute allowManager={true}><ShopAssistantPage /></RoleProtectedRoute>} />
              <Route path="shop-orders" element={<ShopOrdersPage />} />
            </Route>
          </Routes>
          <Toaster 
            theme="dark" 
            position="top-right"
            toastOptions={{
              style: {
                background: 'hsl(222.2 47.4% 11.2%)',
                border: '1px solid hsl(217.2 32.6% 17.5%)',
                color: 'hsl(210 40% 98%)',
              },
            }}
          />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
